import { useEffect, useState } from 'react';

import { PipelineTable } from '../components/pipeline/PipelineTable';
import { apiClient, type Custodian, type Opportunity } from '../lib/api';

/**
 * "Opportunités" page (route /leads in the nav model).
 *
 * Renders the same dense Excel-like PipelineTable as the Pipeline view's
 * "Tableau" tab — including inline editing, per-column filters/sort, sticky
 * header + first column + totals row, computed RoA columns with tooltips,
 * Excel export. This page differs from /pipeline by NOT offering the
 * Kanban/Liste toggles: it's the focused, list-first reading mode for
 * the same dataset.
 */
export function LeadsPage() {
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [custodians, setCustodians] = useState<Custodian[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [rows, banks] = await Promise.all([
          apiClient.listOpportunities(),
          apiClient.listCustodians().catch(() => [] as Custodian[]),
        ]);
        if (!cancelled) {
          setOpportunities(rows);
          setCustodians(banks);
        }
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  function handlePatched(updated: Opportunity) {
    setOpportunities((curr) => curr.map((o) => (o.id === updated.id ? updated : o)));
  }

  return (
    <div className="px-8 pt-10">
      <header className="mb-6">
        <p className="label-uppercase">— 04 / Opportunities</p>
        <h1 className="mt-2 font-display text-4xl text-graphite font-semibold">
          All opportunities
        </h1>
        <p className="mt-2 text-sm text-ink-secondary">
          {opportunities.length} opportunities · inline editing · column filters · Excel export
        </p>
      </header>

      {loading && <div className="px-8 pb-10 text-sm text-ink-tertiary">Loading…</div>}
      {error && (
        <div className="px-8 pb-10 text-sm text-semantic-error" role="alert">
          {error}
        </div>
      )}
      {!loading && !error && (
        <PipelineTable
          opportunities={opportunities}
          custodians={custodians}
          onPatched={handlePatched}
        />
      )}
    </div>
  );
}
